"use client";

import * as React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const steps = [{
  title: "Initial Assessment",
  description: "We conduct a thorough evaluation of your spine and nervous system to identify the root cause of discomfort and functional limitations."
}, {
  title: "Tailored Care Plan",
  description: "Our experts design a specific recovery roadmap based on your unique lifestyle, health goals, and precise diagnostic findings."
}, {
  title: "Clinical Excellence",
  description: "Experience precise, gentle adjustments delivered with advanced techniques to restore natural alignment, mobility, and nervous system function."
}, {
  title: "Sustained Wellness",
  description: "Transition into proactive care with personalized corrective exercises and ergonomic advice to maintain your health long-term."
}];
function StepItem({
  step,
  index,
  isLast
}: {
  step: (typeof steps)[0];
  index: number;
  isLast: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <div data-section-id="189761" ref={ref} data-index={index} className={cn("relative flex gap-6 transition-all duration-700 ease-out", isVisible ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0", isLast ? "pb-0" : "pb-12 md:pb-16")}>
      {}
      {!isLast && <div className="absolute bottom-0 left-5 top-10 w-px -translate-x-1/2 bg-secondary/30" />}
      
      {}
      <div className="relative z-10 flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xl font-serif font-medium text-primary shadow-none">
        {String(index + 1).padStart(2, '0')}
      </div>
      
      {}
      <div className="flex flex-col pt-1.5">
        <h3 className="mb-2 text-xl font-serif font-semibold text-foreground md:text-2xl">
          {step.title}
        </h3>
        <p className="text-lg leading-relaxed font-sans text-muted-foreground md:text-xl">
          {step.description}
        </p>
      </div>
    </div>;
}
export default function HowItWorks() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="how-it-works" className="bg-background py-16 md:py-24 lg:py-32">
      <div className="container mx-auto max-w-3xl px-4 md:px-6">
        <div ref={headerRef} className={cn("mb-12 transition-all duration-700 ease-out md:mb-16", isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0")}>
          <h2 className="mb-4 text-3xl font-serif font-bold tracking-tight text-foreground md:text-4xl lg:text-5xl">
            Your Path to Vitality
          </h2>
          <p className="text-lg font-sans text-muted-foreground md:text-xl">
            A structured, patient-centered approach to restoring your body's natural alignment and long-term health.
          </p>
        </div>

        <div className="relative">
          {steps.map((step, index) => <StepItem key={index} step={step} index={index} isLast={index === steps.length - 1} />)}
        </div>
      </div>
    </section>;
}